import { useToast } from "@chakra-ui/react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { mutationErrorHandler } from "am-tax-fe-core";

import { TOAST_DURATION } from "../../utils/constants";

import { getCurrentUser, updateSystemAdminStatus } from "../entities/entityApi";
import { queryKeys } from "../queryKeys";
import type { components } from "../generated/generated-api";

type UpdateSystemAdminRequest = components["schemas"]["UpdateSystemAdminRequest"];

export function useCurrentUser() {
    const toast = useToast();
    return useQuery({
        ...queryKeys.users.current,
        queryFn: async () => {
            const user = await getCurrentUser()
                .then(user => {
                    return user;
                })
                .then(res => res)
                .catch(err => {
                    toast({
                        title: "Authentication Error",
                        description: "Unable to retrieve current user information.",
                        status: "error",
                        duration: TOAST_DURATION,
                        isClosable: true,
                    });
                    throw err; // Re-throw to maintain error state
                });
            return user;
        },
        retry: false, // Don't retry auth failures
        staleTime: 5 * 60 * 1000, // Consider user data stale after 5 minutes
    });
}

export function useUpdateSystemAdminStatus() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async (args: { azureAdObjectId: string; request: UpdateSystemAdminRequest }) => {
            return await updateSystemAdminStatus(args.azureAdObjectId, args.request);
        },
        onSuccess: async () => {
            // Invalidate all users queries to refresh the list
            await queryClient.invalidateQueries(queryKeys.users.all);
            // Also invalidate current user in case we're updating our own status
            await queryClient.invalidateQueries(queryKeys.users.current);
            toast({
                title: "Updated",
                description: "User system admin status updated successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        ...mutationErrorHandler(toast, "Unable to update user system admin status"),
    });
}
